/**
 * Frontend Scale Service
 * Communicates with Electron main process to handle scale operations
 */

const scaleService = {
    isElectron() {
        return !!(window.electronBridge && window.electronBridge.hardwareInvoke);
    },

    /**
     * List all available serial ports
     * @returns {Promise<{ok: boolean, ports: Array, error: string}>}
     */
    async listPorts() {
        if (!this.isElectron()) {
            console.warn('Scale service only available in Electron environment');
            return { ok: false, error: 'Not supported on this platform' };
        }
        try {
            return await window.electronBridge.hardwareInvoke('scale:listPorts');
        } catch (error) {
            console.error('Failed to list ports:', error);
            return { ok: false, error: error.message };
        }
    },

    /**
     * Connect to the scale
     * @param {string} port - COM port path (e.g., 'COM2')
     * @param {number} baudRate - Baud rate (default: 9600)
     * @param {number} dataBits - Data bits (default: 8)
     * @param {string} parity - Parity (default: 'none')
     * @param {number} stopBits - Stop bits (default: 1)
     * @param {string} scaleModel - Scale model/protocol (default: 'standard')
     * @param {number} scaleDecimal - Decimal places for custom parsing (default: 3)
     * @returns {Promise<{ok: boolean, message: string, error: string}>}
     */
    async connect(port, baudRate = 9600, dataBits = 8, parity = 'none', stopBits = 1, scaleModel = 'standard', scaleDecimal = 3) {
        if (!this.isElectron()) {
            return { ok: false, error: 'Not supported on this platform' };
        }
        try {
            // Note: IPC handler expects 'portPath', receiving 'port' arg here
            return await window.electronBridge.hardwareInvoke('scale:connect', { portPath: port, baudRate, dataBits, parity, stopBits, scaleModel, scaleDecimal });
        } catch (error) {
            console.error('Failed to connect to scale:', error);
            return { ok: false, error: error.message };
        }
    },

    /**
     * Disconnect from the scale
     * @returns {Promise<{ok: boolean, message: string, error: string}>}
     */
    async disconnect() {
        if (!this.isElectron()) {
            return { ok: false, error: 'Not supported on this platform' };
        }
        try {
            return await window.electronBridge.hardwareInvoke('scale:disconnect');
        } catch (error) {
            console.error('Failed to disconnect from scale:', error);
            return { ok: false, error: error.message };
        }
    },

    /**
     * Subscribe to live scale data (RAW)
     * @param {Function} callback - Function(data) called when data arrives
     * @returns {Function} unsubscribe - Function to remove listener
     */
    onData(callback) {
        if (!this.isElectron()) return () => { };
        if (window.electronBridge && window.electronBridge.onScaleData) {
            return window.electronBridge.onScaleData(callback);
        }
        return () => { };
    },

    /**
     * Read weight from the scale (Atomic Capture)
     * Connects, waits 1s for stable reading, then disconnects.
     * Returns "data" array to match frontend raw parsing logic.
     * @param {string} port - COM port path
     * @param {number} baudRate - Baud rate
     * @param {number} dataBits - Data bits
     * @param {string} parity - Parity
     * @param {string} scaleModel - Scale model/protocol
     * @param {number} scaleDecimal - Decimal places for custom parsing
     */
    async capture(port, baudRate = 9600, dataBits = 8, parity = 'none', scaleModel = 'standard', scaleDecimal = 3) {
        if (!this.isElectron()) {
            // Mock for browser testing
            if (process.env.NODE_ENV === 'development') {
                return new Promise(resolve => {
                    setTimeout(() => {
                        resolve({ ok: true, data: [`${(Math.random() * 5).toFixed(3)}`] });
                    }, 1000);
                });
            }
            return { ok: false, error: 'Not supported on this platform' };
        }

        try {
            console.log('[scaleService.capture] ===== SCALE CAPTURE CALL =====');
            console.log('[scaleService.capture] Port:', port, '| BaudRate:', baudRate, '| DataBits:', dataBits, '| Parity:', parity, '| ScaleModel:', scaleModel, '| ScaleDecimal:', scaleDecimal);

            // 1. Connect
            const connRes = await this.connect(port, baudRate, dataBits, parity, 1, scaleModel, scaleDecimal);
            console.log('[scaleService.capture] Connect result:', connRes);
            if (!connRes.ok) return connRes;

            // 2. Read Weight (Backend waits 1s)
            const readRes = await this.readWeight();
            console.log('[scaleService.capture] Read result:', readRes);

            // 3. Disconnect
            await this.disconnect();

            // 4. Transform result for compatibility
            if (readRes.ok) {
                // Frontend expects { ok: true, data: ["1.234"] } to regex match /([\d]+\.[\d]+)/
                // output from backend readWeight is { ok: true, weight: 1.234, unit: 'kg' }
                const result = { ok: true, data: [`${readRes.weight}`] };
                console.log('[scaleService.capture] Final result:', result);
                return result;
            }
            return readRes;

        } catch (error) {
            console.error('Failed to capture weight:', error);
            await this.disconnect(); // Ensure disconnect on error
            return { ok: false, error: error.message };
        }
    },

    /**
     * Read weight from the scale (Direct IPC call)
     * @returns {Promise<{ok: boolean, weight: number, unit: string, error: string}>}
     */
    async readWeight() {
        try {
            return await window.electronBridge.hardwareInvoke('scale:readWeight');
        } catch (error) {
            console.error('Failed to read weight:', error);
            return { ok: false, error: error.message };
        }
    }
};

export default scaleService;
